import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Loader2, Search } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Loader, fmt, StatusBadge, fmtDateTime, EmptyState } from "@/components/shared";
import { AdminPageHeader, ApproveButton, RejectButton } from "./adminShared";

const field = "w-full bg-slate-900/60 border border-cyan-500/20 rounded-lg px-4 py-2.5 text-slate-100 focus:border-cyan-400 focus:outline-none";
const tabs = [["settings", "Share Settings"], ["purchases", "Purchases"], ["holdings", "Ownership"], ["transactions", "Transactions"]];

function SettingsTab({ info, reload }) {
  const [price, setPrice] = useState(info.share_price);
  const [avail, setAvail] = useState(info.available_shares);
  const [enabled, setEnabled] = useState(info.sales_enabled);
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try { await api.put("/admin/shares/settings", { share_price: parseFloat(price), available_shares: parseInt(avail, 10), sales_enabled: enabled }); toast.success("Share settings saved"); reload(); }
    catch (e) { toast.error(formatApiError(e)); } finally { setSaving(false); }
  };
  return (
    <div className="grid lg:grid-cols-3 gap-6" data-testid="share-settings-tab">
      <div className="glass rounded-2xl p-6 space-y-4 lg:col-span-2">
        <h3 className="font-heading text-lg text-white">Share Price & Inventory</h3>
        <div className="grid sm:grid-cols-2 gap-4">
          <div><label className="text-sm text-slate-300 mb-1.5 block">Share Price (USDT)</label><input data-testid="share-setting-price" type="number" step="0.01" min="0.01" value={price} onChange={(e) => setPrice(e.target.value)} className={field} /></div>
          <div><label className="text-sm text-slate-300 mb-1.5 block">Available Shares (unsold inventory)</label><input data-testid="share-setting-available" type="number" min="0" value={avail} onChange={(e) => setAvail(e.target.value)} className={field} /></div>
        </div>
        <label className="flex items-center gap-3 cursor-pointer">
          <input data-testid="share-setting-enabled" type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} className="w-4 h-4 accent-cyan-400" />
          <span className="text-sm text-slate-200">Share sales enabled</span>
        </label>
        <p className="text-xs text-slate-500">Reserved shares ({info.reserved_shares}) are held by pending requests and cannot be oversold. Sold shares ({info.sold_shares}) are confirmed ownership.</p>
        <button onClick={save} disabled={saving} data-testid="share-settings-save" className="btn-glow px-5 py-2.5 rounded-lg text-sm font-semibold text-slate-950 inline-flex items-center gap-2 disabled:opacity-60">{saving && <Loader2 className="w-4 h-4 animate-spin" />} Save Settings</button>
      </div>
      <div className="glass rounded-2xl p-6 space-y-4">
        {[["Share Price", `${fmt(info.share_price)} USDT`], ["Available", info.available_shares], ["Reserved (pending)", info.reserved_shares], ["Sold", info.sold_shares], ["Total", info.total_shares], ["Sales", info.sales_enabled ? "Open" : "Closed"]].map(([k, v]) => (
          <div key={k} className="flex justify-between text-sm border-b border-white/5 pb-2 last:border-0"><span className="text-slate-400">{k}</span><span className="font-mono text-white" data-testid={`share-info-${k.toLowerCase().split(" ")[0]}`}>{v}</span></div>
        ))}
      </div>
    </div>
  );
}

function PurchasesTab() {
  const [items, setItems] = useState(null);
  const [status, setStatus] = useState("pending");
  const [search, setSearch] = useState("");
  const load = useCallback(async () => { setItems(null); const r = await api.get("/admin/shares/purchases", { params: { status, search } }); setItems(r.data); }, [status, search]);
  useEffect(() => { load(); }, [load]);
  const approve = async (id) => { try { await api.post(`/admin/shares/purchases/${id}/approve`, {}); toast.success("Purchase approved"); load(); } catch (e) { toast.error(formatApiError(e)); } };
  const reject = async (id, reason) => { try { await api.post(`/admin/shares/purchases/${id}/reject`, { reason }); toast.success("Purchase rejected"); load(); } catch (e) { toast.error(formatApiError(e)); } };
  return (
    <div data-testid="share-purchases-tab">
      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <div className="flex gap-2 flex-wrap">{["all", "pending", "approved", "rejected"].map((f) => (
          <button key={f} data-testid={`purchase-filter-${f}`} onClick={() => setStatus(f)} className={`px-4 py-2 rounded-lg text-sm border capitalize transition-colors ${status === f ? "bg-cyan-500/15 border-cyan-500/40 text-cyan-300" : "border-white/10 text-slate-400 hover:border-cyan-500/20"}`}>{f}</button>))}</div>
        <div className="relative sm:ml-auto"><Search className="w-4 h-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" /><input data-testid="purchase-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search user..." className={`${field} pl-9 py-2`} /></div>
      </div>
      {items === null ? <Loader /> : items.length ? (
        <div className="glass rounded-2xl p-4 overflow-x-auto">
          <table className="w-full text-sm min-w-[900px]">
            <thead><tr className="text-left text-slate-500 border-b border-white/5">{["Ref", "User", "Shares", "Price", "Total", "Source", "Date", "Status", "Actions"].map((h) => <th key={h} className="pb-3 px-2 font-medium">{h}</th>)}</tr></thead>
            <tbody>{items.map((p) => (
              <tr key={p.id} className="border-b border-white/5 last:border-0" data-testid={`admin-purchase-row-${p.id}`}>
                <td className="py-3 px-2 font-mono text-xs text-slate-500">{p.id.slice(0, 8)}</td>
                <td className="py-3 px-2"><p className="text-slate-200">{p.user_name}</p><p className="text-xs text-slate-500">{p.user_email}</p></td>
                <td className="py-3 px-2 font-mono text-white">{p.quantity}</td>
                <td className="py-3 px-2 font-mono text-slate-300">{fmt(p.share_price)}</td>
                <td className="py-3 px-2 font-mono text-white">{fmt(p.total)}</td>
                <td className="py-3 px-2 text-slate-300 capitalize">{p.source}</td>
                <td className="py-3 px-2 text-slate-500">{fmtDateTime(p.created_at)}</td>
                <td className="py-3 px-2"><StatusBadge status={p.status} />{p.admin_note && <p className="text-xs text-slate-500 mt-1 max-w-[140px]">{p.admin_note}</p>}</td>
                <td className="py-3 px-2">{p.status === "pending" ? <div className="flex gap-2"><ApproveButton testId={`purchase-approve-${p.id}`} onConfirm={() => approve(p.id)} /><RejectButton testId={`purchase-reject-${p.id}`} onConfirm={(r) => reject(p.id, r)} /></div> : <span className="text-xs text-slate-600">by {p.reviewed_by || "-"}</span>}</td>
              </tr>))}</tbody>
          </table>
        </div>
      ) : <EmptyState title="No purchases" subtitle={`No ${status} share purchases found.`} />}
    </div>
  );
}

function HoldingsTab() {
  const [data, setData] = useState(null);
  const [search, setSearch] = useState("");
  useEffect(() => { api.get("/admin/shares/holdings", { params: { search } }).then((r) => setData(r.data)); }, [search]);
  return (
    <div data-testid="share-holdings-tab">
      <div className="flex flex-col sm:flex-row gap-3 mb-4 items-start sm:items-center">
        {data && <p className="text-sm text-slate-400">{data.totals.holders} holders · <span className="font-mono text-white">{data.totals.shares}</span> shares · <span className="font-mono text-cyan-300">{fmt(data.totals.value)}</span> USDT</p>}
        <div className="relative sm:ml-auto"><Search className="w-4 h-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" /><input data-testid="holdings-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search user..." className={`${field} pl-9 py-2`} /></div>
      </div>
      {!data ? <Loader /> : data.holders.length ? (
        <div className="glass rounded-2xl p-4 overflow-x-auto">
          <table className="w-full text-sm min-w-[700px]">
            <thead><tr className="text-left text-slate-500 border-b border-white/5">{["User", "Code", "Shares", "Total Paid (USDT)", "Current Value", "Updated"].map((h) => <th key={h} className="pb-3 px-2 font-medium">{h}</th>)}</tr></thead>
            <tbody>{data.holders.map((h) => (
              <tr key={h.user_id} className="border-b border-white/5 last:border-0" data-testid={`holding-row-${h.user_id}`}>
                <td className="py-3 px-2"><p className="text-slate-200">{h.user_name}</p><p className="text-xs text-slate-500">{h.user_email}</p></td>
                <td className="py-3 px-2 font-mono text-xs text-slate-400">{h.user_code}</td>
                <td className="py-3 px-2 font-mono text-white">{h.shares}</td>
                <td className="py-3 px-2 font-mono text-slate-300">{fmt(h.total_value)}</td>
                <td className="py-3 px-2 font-mono text-cyan-300">{fmt(h.shares * data.info.share_price)}</td>
                <td className="py-3 px-2 text-slate-500">{fmtDateTime(h.updated_at)}</td>
              </tr>))}</tbody>
          </table>
        </div>
      ) : <EmptyState title="No shareholders yet" />}
    </div>
  );
}

function TransactionsTab() {
  const [items, setItems] = useState(null);
  useEffect(() => { api.get("/admin/shares/transactions").then((r) => setItems(r.data)); }, []);
  if (!items) return <Loader />;
  return items.length ? (
    <div className="glass rounded-2xl p-4 overflow-x-auto" data-testid="share-transactions-tab">
      <table className="w-full text-sm min-w-[760px]">
        <thead><tr className="text-left text-slate-500 border-b border-white/5">{["Date", "User", "Type", "Amount", "Status", "Description", "Ref"].map((h) => <th key={h} className="pb-3 px-2 font-medium">{h}</th>)}</tr></thead>
        <tbody>{items.map((t) => (
          <tr key={t.id} className="border-b border-white/5 last:border-0">
            <td className="py-3 px-2 text-slate-500">{fmtDateTime(t.created_at)}</td>
            <td className="py-3 px-2"><p className="text-slate-200">{t.user_name}</p><p className="text-xs text-slate-500">{t.user_email}</p></td>
            <td className="py-3 px-2 text-slate-300 capitalize">{t.type.replace(/_/g, " ")}</td>
            <td className="py-3 px-2 font-mono text-white">{fmt(t.amount)}</td>
            <td className="py-3 px-2"><StatusBadge status={t.status} /></td>
            <td className="py-3 px-2 text-slate-400 text-xs">{t.description}</td>
            <td className="py-3 px-2 font-mono text-xs text-slate-500">{(t.reference_id || "").slice(0, 8)}</td>
          </tr>))}</tbody>
      </table>
    </div>
  ) : <EmptyState title="No share transactions yet" />;
}

export default function AdminShares() {
  const [tab, setTab] = useState("settings");
  const [info, setInfo] = useState(null);
  const reload = useCallback(() => api.get("/admin/shares/settings").then((r) => setInfo(r.data)), []);
  useEffect(() => { reload(); }, [reload]);
  return (
    <div data-testid="admin-shares">
      <AdminPageHeader eyebrow="Management" title="Share Management" subtitle="Price, inventory, purchase approvals and shareholder ownership."
        right={<div className="flex gap-2 flex-wrap">{tabs.map(([v, l]) => (
          <button key={v} data-testid={`shares-tab-${v}`} onClick={() => setTab(v)} className={`px-4 py-2 rounded-lg text-sm border transition-colors ${tab === v ? "bg-cyan-500/15 border-cyan-500/40 text-cyan-300" : "border-white/10 text-slate-400 hover:border-cyan-500/20"}`}>{l}</button>))}</div>} />
      {tab === "settings" && (info ? <SettingsTab key={info.updated_at} info={info} reload={reload} /> : <Loader />)}
      {tab === "purchases" && <PurchasesTab />}
      {tab === "holdings" && <HoldingsTab />}
      {tab === "transactions" && <TransactionsTab />}
    </div>
  );
}
